import type {
  OpeningProfileLoops2,
  OpeningSideLayer,
  OpeningSide,
  OpeningSideName,
} from "building-types/openings/openingTypes";
import { BACK, FRONT } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { PolyLine2sWithPlane } from "building-types/walls/PolyLine2sWithPlane";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { roomIsEffectivelyExterior } from "doc/rooms/roomQuery";
import type { AABB3 } from "geom/aabb3Types";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import type { OBB2 } from "geom/obb2";
import { obb2New } from "geom/obb2";
import { offsetPolyLine2SSkFallback } from "geom/offset";
import { planeNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Centroid,
  polyLine2Close,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgesCollinearToPolyLine2,
  polyLine2EdgeStart,
  polyLine2IsClosed,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3AABB3, polyLine3OnPlane } from "geom/polyLine3";
import type { Rect2 } from "geom/rect2Types";
import { vec2Len } from "geom/vec2";
import { vec3Dist } from "geom/vec3";
import { profile2Offset, profile2OrientCopy } from "../profile2Utils";

export function isValidOpening(opening: WallOpening | undefined): opening is WallOpening {
  // NOTE: add more checks as needed to validate an opening
  if (!opening) {
    return false;
  }
  return true;
}

export function wallOpeningBottomZ(opening: WallOpening): WorldInches {
  const {
    front: { opening3: frontOutline3 },
    back: { opening3: backOutline3 },
  } = opening;

  if (!frontOutline3 || !backOutline3) {
    return worldInchesNew(0);
  }

  const aabb3FrontOutline = polyLine3AABB3(frontOutline3);
  const aabb3BackOutline = polyLine3AABB3(backOutline3);

  return worldInchesNew(Math.min(aabb3FrontOutline.minZ, aabb3BackOutline.minZ));
}

export function wallOpeningDepth(opening: WallOpening): WorldInches {
  const { frontPlane, backPlane } = opening;

  const [nx, ny, nz] = planeNormal(backPlane);
  const [dx, dy, dz] = ensureDefined(
    intersectRay3PlaneWithNormal(
      backPlane.originX,
      backPlane.originY,
      backPlane.originZ,
      -nx,
      -ny,
      -nz,
      frontPlane.originX,
      frontPlane.originY,
      frontPlane.originZ,
      nx,
      ny,
      nz,
    ),
  );

  const openingDepth = vec3Dist(dx, dy, dz, backPlane.originX, backPlane.originY, backPlane.originZ);
  return worldInchesNew(openingDepth);
}

export function wallOpeningSideIsExterior(
  opening: WallOpening,
  side: OpeningSideName,
  includeExteriorRooms: boolean = false,
): boolean {
  const room = side === FRONT ? opening.frontRoom : opening.backRoom;
  return roomIsEffectivelyExterior(room, includeExteriorRooms);
}

export function wallOpeningHasExteriorSide(opening: WallOpening, includeExteriorRooms: boolean = false): boolean {
  return (
    wallOpeningSideIsExterior(opening, FRONT, includeExteriorRooms) ||
    wallOpeningSideIsExterior(opening, BACK, includeExteriorRooms)
  );
}

export function wallOpeningHasSill(opening: WallOpening): boolean {
  return opening.hasSill ?? true;
}

export function wallOpeningHasWrapping(opening: WallOpening): boolean {
  return opening.hasWrapping ?? true;
}

export function wallOpeningSide(opening: WallOpening, side: OpeningSideName): OpeningSide {
  return side === FRONT ? opening.front : opening.back;
}

export function wallOpeningSideLayer(opening: WallOpening, side: OpeningSideName, layerId: string): OpeningSideLayer {
  return openingSideLayer(wallOpeningSide(opening, side), layerId);
}

export function wallOpeningOpening2(
  opening: WallOpening,
  side: OpeningSideName,
  includeCasing: boolean = true,
): PolyLine2 {
  return openingSideOpening2(wallOpeningSide(opening, side), includeCasing);
}

export function wallOpeningUnion2(opening: WallOpening, side: OpeningSideName, layerId: string): PolyLine2 | undefined {
  return openingSideLayerUnion2(wallOpeningSide(opening, side), layerId);
}

export function wallOpeningUnionEdges2(
  opening: WallOpening,
  side: OpeningSideName,
  layerId: string,
): PolyLine2sWithPlane | undefined {
  return openingSideLayerUnionEdges(wallOpeningSide(opening, side), layerId);
}

export function wallOpeningPlane(opening: WallOpening, side: OpeningSideName): Plane {
  return side === FRONT ? opening.frontPlane : opening.backPlane;
}

export function wallOpeningOffset2(opening: WallOpening, side: OpeningSideName, depth: number): PolyLine2 {
  if (depth === 0) {
    return wallOpeningOpening2(opening, side);
  }

  const openingSide = wallOpeningSide(opening, side);

  const name = `${depth}`;
  if (openingSide.openingOffsets2[name] === undefined) {
    // in the context of a wall opening, a positive offset is *inwards*, so we reverse it here
    const inverted = -depth;

    let offset: PolyLine2;
    if (wallOpeningHasSill(opening)) {
      offset = profile2Offset(wallOpeningOpening2(opening, side, false), inverted);
      if (offset.length !== 0) {
        offset = polyLine2Close(polyLine2SetCW(offset));
      }
    } else {
      offset = offsetPolyLine2SSkFallback(polyLine2SetCW(polyLine2Copy(wallOpeningOpening2(opening, side))), inverted);
    }

    openingSide.openingOffsets2[name] = profile2OrientCopy(polyLine2SetCCW(offset));
  }

  return openingSide.openingOffsets2[name] || wallOpeningOpening2(opening, side); // fallback to opening2
}

export function wallOpeningRect2(opening: WallOpening, side: OpeningSideName): PolyLine2 {
  return wallOpeningSide(opening, side).openingRect2;
}

export function wallOpeningOpening3(
  opening: WallOpening,
  side: OpeningSideName,
  includeCasing: boolean = true,
): PolyLine3 | undefined {
  return openingSideOpening3(wallOpeningSide(opening, side), includeCasing);
}

export function wallOpeningOffset3(opening: WallOpening, side: OpeningSideName, depth: number): PolyLine3 | undefined {
  if (depth === 0) {
    return wallOpeningOpening3(opening, side);
  }

  const name = `${depth}`;

  const profile = wallOpeningSide(opening, side);

  if (profile.openingOffsets3[name] === undefined) {
    const plane = wallOpeningPlane(opening, side);
    profile.openingOffsets3[name] = polyLine3OnPlane(wallOpeningOffset2(opening, side, depth), plane);
  }

  return profile.openingOffsets3[name] || wallOpeningOpening3(opening, side); // fallback to opening3
}

export function wallOpeningHoleEdges3(
  opening: WallOpening,
  side: OpeningSideName,
  layerId: string,
  depth: number,
): PolyLine3[] {
  const plane = wallOpeningPlane(opening, side);
  const edges = wallOpeningHoleEdges2(opening, side, layerId, depth);
  return edges.map((edge) => polyLine3OnPlane(edge, plane));
}

export function wallOpeningAABB3(opening: WallOpening, side: OpeningSideName): AABB3 | undefined {
  const profile = wallOpeningSide(opening, side);
  if (!profile.aabb3) {
    const opening3 = wallOpeningOpening3(opening, side);
    if (opening3) {
      profile.aabb3 = polyLine3AABB3(opening3);
    }
  }
  return profile.aabb3;
}

export function wallOpeningShouldMirror(opening: WallOpening): boolean {
  return opening.mirror;
}

export function openingSideOpening2(profiles: OpeningSide, includeCasing: boolean = true): PolyLine2 {
  let opening: PolyLine2;
  if (includeCasing && !!profiles.hasCasing && profiles.openingCasing2) {
    opening = profiles.openingCasing2;
    if (!(profiles.openingCasing2.length >= 8)) {
      console.assert(false, "openingCasing2 should have at least three points");
    }
    if (!(polyLine2IsClosed(opening))) {
      console.assert(false, "openingCasing2 should be closed");
    }
  } else {
    opening = profiles.opening2;
    if (!(opening.length >= 8)) {
      console.assert(false, "opening2 should have at least three points");
    }
    if (!(polyLine2IsClosed(opening))) {
      console.assert(false, "opening2 should be closed");
    }
  }

  // @todo(Andrew): determine why some opening loops are not closed properly or close them here
  // if (!polyLine2IsClosed(opening)) {
  //   opening = polyLine2Close(opening);
  // }

  opening = profile2OrientCopy(polyLine2SetCCW(polyLine2Copy(opening)));

  return opening;
}

export function openingSideOffset2(profiles: OpeningSide, depth: number): PolyLine2 {
  if (depth === 0) {
    return openingSideOpening2(profiles);
  }

  if (profiles.openingOffsets2[depth] === undefined) {
    let offset: PolyLine2;
    if (profiles.hasCasing) {
      offset = profile2Offset(openingSideOpening2(profiles, false), depth);
    } else {
      offset = offsetPolyLine2SSkFallback(openingSideOpening2(profiles, false), depth);
    }

    profiles.openingOffsets2[depth] = profile2OrientCopy(polyLine2SetCCW(offset));
  }

  return profiles.openingOffsets2[depth] || openingSideOpening2(profiles, false); // fallback to opening2
}

export function openingSideOpening3(side: OpeningSide, includeCasing: boolean = true): PolyLine3 | undefined {
  if (includeCasing && !!side.hasCasing && side.openingCasing3) {
    return side.openingCasing3;
  }
  return side.opening3;
}

export function wallOpeningPlanExtents(opening: WallOpening): Rect2 {
  // @TODO(Andrew): lazily calculate this and cache it
  return opening.planExtents;
}

export function wallOpeningObb({ planExtents }: WallOpening): OBB2 | null {
  // planExtends is expected to be a 2D rectangle
  if (!(polyLine2IsClosed(planExtents))) {
    console.assert(false, "planExtents should be closed");
  }
  if (!(polyLine2EdgeCount(planExtents) === 4)) {
    console.assert(false, "planExtents should have 4 edges");
  }

  const [x0, y0] = polyLine2EdgeStart(planExtents, 0);
  const [x1, y1] = polyLine2EdgeEnd(planExtents, 0);
  const [x2, y2] = polyLine2EdgeEnd(planExtents, 1);

  const ux = x1 - x0;
  const uy = y1 - y0;
  const vx = x2 - x1;
  const vy = y2 - y1;

  const halfWidth = vec2Len(ux, uy) / 2;
  const halfHeight = vec2Len(vx, vy) / 2;
  const rotation = Math.atan2(uy, ux);
  const centroid = polyLine2Centroid(planExtents);

  if (!centroid) {
    return null;
  }

  const [centerX, centerY] = centroid;

  return obb2New(centerX, centerY, halfWidth, halfHeight, rotation);
}

export function openingSideLayerUnion2(sideLoops: OpeningProfileLoops2, layerId: string): PolyLine2 | undefined {
  const layer = openingSideLayer(sideLoops, layerId);
  const firstLine = layer.union?.lines[0];
  if (firstLine === undefined) {
    return undefined;
  }
  return profile2OrientCopy(polyLine2SetCCW(firstLine));
}

export function openingSideLayer(side: OpeningProfileLoops2, layerId: string): OpeningSideLayer {
  if (!side.openingSideLayers[layerId]) {
    side.openingSideLayers[layerId] = {};
  }
  return side.openingSideLayers[layerId];
}

export function openingSideLayerUnionEdges(
  side: OpeningProfileLoops2,
  layerId: string,
): PolyLine2sWithPlane | undefined {
  return openingSideLayer(side, layerId)?.lines;
}

export function openingSideHoleEdges2(side: OpeningSide, layerId: string, offset: number = 0): PolyLine2[] {
  const union = openingSideLayerUnion2(side, layerId);
  const opening2 = openingSideOffset2(side, offset);
  if (!union) {
    return [];
  }

  return polyLine2EdgesCollinearToPolyLine2(opening2, union).map((i) => polyLine2Edge(union, i));
}

export function wallOpeningHoleEdges2(
  opening: WallOpening,
  side: OpeningSideName,
  layerId: string,
  offset: number = 0,
): PolyLine2[] {
  const profile = wallOpeningSide(opening, side);
  return openingSideHoleEdges2(profile, layerId, offset);
}
